/**
 * @author gyb(mocheer)
 * @email mocheer@foxmail.com  
 * @since 2016.11.4
 */
import lonlats from '@/utils/LonLats';
import Popup from '@/polyfill/Popup';
import PathMarker from '@/marker/PathMarker';
import Typhoon from "@/marker/Typhoon";
import Ellipse from '@/marker/Ellipse';
import "@/marker/Text";
import '@/marker/Heat';
import River from '@/marker/River';
import MilitaryArrow from '@/marker/MilitaryArrow'
//
let { isFunction, isArray, fmt, assign: $assign } = T;
let { featureGroup: toGroup, marker: toMarker, polyline: toPolyline, polygon: toPolygon, circle: toCircle, tooltip: toTooltip, icon: toIcon, divIcon: toDivIcon, imageOverlay: toImageOverlay } = L;
let toArray = data => isArray(data) ? data : [data];
let toOptions = (options, data) => isFunction(options) ? options(data) : options || {};
/**
 * 要素管理的通用图层
 */
export default class FeatureLayer {
  dmap;
  map;
  info;   // 预留，提供给菜单，用于绑定检索
  options;// 样式，图层配置
  /**
   * 
   * @param {*} options 
   */
  constructor(options) {
    // 台风模块需要设置预报机构
    this.config(options);
  }
  /**
   * 配置
   * @param options 
   */
  config(options) {
    this.options = options;
    this.layer = toGroup();
  }
  /**
   * 判断图元是否存在
   * @param {*} marker 
   */
  has(marker) {
    return this.layer.hasLayer(marker)
  }
  /**
   * 获取所有图层
   */
  getMarkers() {
    return this.layer.getLayers();
  }
  /**
   * 添加图元
   * @param {*} marker 
   */
  _add(marker) {
    this.layer.addLayer(marker);
  }
  /**
   * 添加图元，并绑定tooltip和popup
   * @param marker 
   */
  add(marker, options, data) {
    if (marker) {
      marker.__data = data;
      this._add(marker);
      this._bindToolTip(marker, options, data);
      this._bindPopup(marker, options, data);
    }
  }
  /**
   * 添加点图元
   * @param data [{coordinates:[lon,lat] or [{lon,lat}] or [{lat,lng}]
   * @param style
   */
  addPoints(data, options) {
    return toArray(data).map(item => {
      let markerOptions = toOptions(options, item);
      let marker = this.createPoint(item, markerOptions);
      this.add(marker, markerOptions, item);
      return marker;
    });
  }
  /**
   * 添加聚合点
   * @param {*} data 
   * @param {*} options 
   */
  addClusterPoints(data, options) {
    let group = L.markerClusterGroup();
    let { layer, _bindToolTip, _bindPopup } = this;
    layer.addLayer(group);
    return toArray(data).map(item => {
      let markerOptions = toOptions(options, item);
      let marker = this.createPoint(item, markerOptions);
      if (marker) {
        marker.__data = item;
        _bindToolTip(marker, markerOptions, item);
        _bindPopup(marker, markerOptions, item);
        group.addLayer(marker);
      }
      return marker;
    });
  }
  /**
   * 添加线图元
   * @param {*} data
   * @param {*} options
   */
  addPolylines(data, options) {
    return toArray(data).map(item => {
      let markerOptions = toOptions(options, item);
      let marker = this.createPolyLine(item, markerOptions);
      this.add(marker, markerOptions, item);
      marker.bringToBack(); //暂时底部 
      return marker;
    });
  }
  /**
   * 添加多边形图元
   * @param {*} data 
   * @param {*} options 
   */
  addPolygons(data, options) {
    return toArray(data).map(item => {
      let markerOptions = toOptions(options, item);
      let marker = this.createPolygon(item, markerOptions);
      this.add(marker, markerOptions, item);
      marker.bringToBack(); //暂时底部
      return marker;
    });
  }
  /**
   * 添加圆形图元
   * @param {*} data 
   * @param {*} options 
   *  
   */
  addCircles(data, options) {
    return toArray(data).map(item => {
      let markerOptions = toOptions(options, item);
      let marker = this.createCircle(item, markerOptions);
      this.add(marker, markerOptions, item);
      marker.bringToBack() // 暂时底部
    });
  }
  /**
   * 添加椭圆
   * @param {*} data 
   * @param {*} options 
   */
  addEllipses(data, options) {
    return toArray(data).map(item => {
      let markerOptions = toOptions(options, item);
      let marker = this.createEllipse(item, markerOptions);
      this.add(marker, markerOptions, item);
      marker.bringToBack() // 暂时底部
    });
  }

  /**
   * 添加文本图元
   * @param {*} data 
   * @param {*} options 
   */
  addTexts(data, options) {
    return toArray(data).map(item => {
      let markerOptions = toOptions(options, item);
      let marker = L.text(lonlats(item.coordinates || item), item.text || item.properties.text, markerOptions.feature || markerOptions);
      marker.__data = item;
      this.layer.addLayer(marker);
    })
  }
  /**
   * 添加河流
   * @param {*} data 
   * @param {*} options 
   */
  addRivers(data, options) {
    return toArray(data).map(item => {
      let markerOptions = toOptions(options, item);
      let { feature } = markerOptions;
      let { coordinates } = item;
      let deep = (coordinates[0] && isArray(coordinates[0][0])) ? 2 : 1;
      let marker = new River(lonlats(coordinates, deep), feature)
      this.layer.addLayer(marker);
    })
  }
  /**
   * 添加图片图元(雷达图、云图)
   * @param {*} data 
   * @param {*} options 
   */
  addImage(data, options) {
    let marker = toImageOverlay(options.url, lonlats(data.coordinates, 1), options);
    this.layer.addLayer(marker);
    return marker;
  }
  /**
   * 
   * @param {*} data 
   * @param {*} options 
   */
  addMilitaryArrows(data, options) {
    return toArray(data).map(item => {
      let markerOptions = toOptions(options, item);
      let { feature } = markerOptions;
      let { coordinates } = data, deep = 1;
      if (coordinates && coordinates[0] && isArray(coordinates[0][0])) {
        deep = 2;
      }
      let marker = new MilitaryArrow(lonlats(coordinates, deep), feature)
      this.add(marker, markerOptions, item);
      marker.bringToBack(); //暂时底部 
      return marker;
    });
  }
  /**
   * 添加台风 
   * @param {*} data 
   * @param {*} options 
   */
  addTyphoon(data, options) {
    let typhoons = this.typhoons || (this.typhoons = {});
    let marker;
    let markerOptions = toOptions(options || this.options);
    if (!typhoons[data.tfbh]) {
      marker = typhoons[data.tfbh] = this.createTyphoon(data, markerOptions)
      marker.addTo(this);
    }
    return typhoons[data.tfbh];
  }
  /**
   * 台风警戒线 
   * @param data {
   *  g24: [[105, 0], [113, 4.5], [119, 11], [119, 18], [127, 22], [127, 34]],
   *  s24: { color: '#0000FF', opacity: 1, weight: 1, dashArray: '10,3', interactive: false },
   *  g48: [[105, 0], [120, 0], [132, 15], [132, 34]],
   *  s48: { color: '#FFFF00', opacity: 1, weight: 1, dashArray: '10,3', interactive: false } 
   * }
   */
  addAlarmLines(data) {
    if (!data) return;
    let { layer } = this;
    let createPolyline = (coor, options) => toPolyline(lonlats(coor, 1), options)
    let m48 = createPolyline(data.g48, data.s48);
    let m24 = createPolyline(data.g24, data.s24);
    layer.addLayer(m48).addLayer(m24);
  }


  /**
   * 添加热力图
   */
  addHeat(data, options) {
    options = toOptions(options);
    let marker = L.heat(data, options.feature);
    return this.add(marker, options, data);
  }
  /**
   * todo:
   * 1、支持4326投影
   * @param data
   * @param options 
   */
  addVelocity(data, options) {
    return new Promise((resolve, reject) => {
      require.ensure([], () => {
        var createLayer = require('@/plugins/velocityLayer').default;
        let marker = createLayer(data,options);
        this._add(marker);
        resolve(marker);
      }, 'velocityLayer')
    })
  }
  /**
   * 添加 shp 图层
   */
  addShp(url, options) {
    let layer = L.shapefile(url, options)
    this._add(layer);
    return layer;
  }
  /**
   * 添加 geojson 图层  
   * @param {*} geojson 
   * @see https://leafletjs.com/reference-1.3.0.html#geojson
   */
  addGeoJSON(geojson, options) {
    let geometry = geojson.type === 'Feature' ? geojson.geometry : geojson;
    let coords = geometry ? geometry.coordinates : null;
    let type = geometry.type;
    if (!coords && !geometry) {
      return null;
    }
    options = geometry.properties || options;
    switch (type) {
      case 'MultiPoint':
        geojson = coords.map(coordinates => {
          return { coordinates: coordinates }
        })
      case 'Point':
        return this.addPoints(geojson, options);
      case 'LineString':
      case 'MultiLineString':
        // { coordinates: lonlats(coords, type === 'LineString' ? 0 : 1) }
        return this.addPolylines(geometry, options)
      case 'Polygon':
      case 'MultiPolygon':
        // { coordinates: lonlats(coords, type === 'Polygon' ? 1 : 2) }
        return this.addPolygons(geometry, options);
      case 'GeometryCollection':
        return geometry.geometries.map(data => this.addGeoJSON(data, options));
      case 'Circle':
        return this.addCircles(geometry, options);
      case 'Ellipse':
        return this.addEllipses(geometry, options);
      case 'Text':
        return this.addTexts(geometry, options);
      case 'MilitaryArrow'://军标箭头
        return this.addMilitaryArrows(geometry, options);
    }
  }
  /**
   * 批量移除台风
   */
  removeTyphoons(ids) {
    let { typhoons } = this;
    if (typhoons) {
      ids = ids || Object.keys(typhoons);
      T.each(ids, id => {
        var typhoon = typhoons[id];
        if (typhoon) {
          typhoon.remove();
          delete typhoons[id];
        }
      })
    }
  }
  /**
   * 获取台风对象
   * @param {*} id
   */
  getTyphoon(id) {
    let { typhoons } = this;
    return id === void 0 ? typhoons : typhoons && typhoons[id];
  }
  /**
   * 创建台风对象
   * @param {*} data
   * @param {*} options 
   */
  createTyphoon(data, options) {
    return new Typhoon(data, options);
  }
  /**
   * 点图元工厂
   * @param {*} data
   * @param {*} options {feature:{icon,symbol,glowOptions,flowOptions}}
   */
  createPoint(data, options) {
    let { feature } = options;
    let { tag, src } = feature || {};
    //
    let latlng = lonlats(data.coordinates || data);
    if (latlng) {
      if (tag) {
        switch (tag) {
          case 'img':
            let anchor = feature.offset;
            if (anchor) {
              anchor = [-anchor[0], -anchor[1]];
            }
            feature.icon = toIcon($assign({
              iconUrl: src,
              iconAnchor: anchor,
              // iconSize: [38, 95],
              // popupAnchor: [-3, -76],
              // shadowUrl: 'shadow.png',
              // shadowSize: [68, 95],
              // shadowAnchor: [22, 94]
            }, feature.icon));
            break;
          case 'div':
            feature.icon = toDivIcon($assign({
              iconAnchor: anchor
            }, feature.icon))
            break;
        }
        return toMarker(latlng, feature);
      }
      return new PathMarker(latlng, feature);
    }
  }
  /**
   * 线图元工厂
   * @param {*} data
   * @param {*} options 
   */
  createPolyLine(data, options) {
    let { feature } = options;
    let { coordinates } = data, deep = 1;
    //todo
    if (coordinates && coordinates[0] && isArray(coordinates[0][0])) {
      deep = 2;
    }
    return toPolyline(lonlats(coordinates, deep), feature);
  }
  /**
   * 多边形图元工厂
   * @param {*} data
   * @param {*} options 
   */
  createPolygon(data, options) {
    let { feature } = options;
    let { coordinates } = data;
    let deep = (coordinates && coordinates[0] && isArray(coordinates[0][0])) ? 2 : 1;
    return toPolygon(lonlats(coordinates, deep), feature);
  }
  /**
   * 圆形图元工厂
   * @param {*} data
   * @param {*} options 
   */
  createCircle(data, options) {
    return toCircle(lonlats(data.coordinates), options.feature);
  }
  /**
   * 
   * @param {*} data 
   * @param {*} options 
   */
  createEllipse(data, options) {
    return new Ellipse(lonlats(data.coordinates), options.feature);
  }
  /**
   * 绑定 tooltip ，浮出层，一般用于显示站名
   * @param marker 
   * @param options 
   * @param data 
   */
  _bindToolTip(marker, options, data) {
    if (options = options.tooltip) {
      let { text } = options;
      let tooltip = typeof text == 'string' ? fmt(text, data || {}) : text;
      //
      if (tooltip) {
        // options.interactive = true;
        tooltip = toTooltip(options, marker).setContent(tooltip);
        marker.bindTooltip(tooltip);
      }
    }
  }
  /**
   * 绑定 popup 弹出层，一般用于显示报图
   * @param marker 
   * @param options 
   */
  _bindPopup(marker, options) {
    options = options.popup;
    if (options) {
      let popup = new Popup(options).setContent(options.text);
      marker.bindPopup(popup, options);
    }
  }
  /**
   * 慎用，这个方法可能会被弃用
   */
  updateTooltips() {
    this.map && this.map.eachLayer(layer => {
      let tooltip = layer.getTooltip();
      tooltip && tooltip.isOpen && tooltip.update()
    });
  }
  /**
   * 隐藏标注，常用于水雨情设置模块
   * @param hide
   */
  hideTooltip(hide) {
    if (hide === void 0) hide = true;
    this.layer.eachLayer(layer => {
      let tooltip = layer.getTooltip();
      if (tooltip) {
        layer.hideTooltip(hide);
      }
    })
  }
  /**
   * 清除
   */
  clear() {
    this.layer.clearLayers();
    this.typhoons = null;
    return this;
  }
  /**
   * 挂起
   */
  suspend() {
    let { map, layer } = this;
    map && map.removeLayer(layer);
    this.suspended = true;
  }
  /**
   * 重启
   */
  resume() {
    let { map, layer } = this;
    map && map.addLayer(layer);
  }
  /**
   * 添加
   * @param dmap 
   */
  addTo(dmap) {
    let that = this;
    let { map } = dmap;
    //
    that.dmap = dmap;
    that.map = map;
    dmap.addLayer(that);
    that.layer.addTo(map);
    return that;
  }
  /**
   * 移除
   */
  remove() {
    let { map, layer } = this;
    map.removeLayer(layer);
    this.dmap = null;
    this.map = null;
  }
  /**
   * 
   * @param types 
   * @param fn 
   * @param ctx 
   */
  on(types, fn, ctx) {
    return this.layer.on(types, fn, ctx);
  }
  /**
   * 
   * @param types 
   * @param fn 
   * @param ctx 
   */
  once(types, fn, ctx) {
    return this.layer.once(types, fn, ctx);
  }
  /**
   * 
   * @param types 
   * @param fn 
   * @param ctx  
   */
  off(types, fn, ctx) {
    return this.layer.off(types, fn, ctx);
  }
  /**
   * 导出 GeoJson
   * @param precision
   */
  toGeoJSON(precision) {
    let { layer } = this;
    let jsons = [];
    layer.eachLayer(layer => {
      if (layer.toGeoJSON) {
        let json = layer.toGeoJSON(precision);
        let { geometry, properties } = json;
        let { __data: data, options } = layer;
        let tooltip = layer.getTooltip();
        geometry.properties = $assign(properties, geometry.properties, data && data.properties, { feature: options, tooltip: tooltip && tooltip.options })
        jsons.push(geometry);
      }
    });
    //
    return {
      geometries: jsons,
      type: 'GeometryCollection'
    };
    //
  }
}


// WEBPACK FOOTER //
// ./src/layer/FeatureLayer.js